import Highcharts from "highcharts";
import { useLayoutEffect } from "react";
import useDebouncedCallback from "../../lib/hooks/useDebouncedCallback";

/**
 * @description
 * Bind an event handler to the chart afterSetExtremes event.
 * The event handler is called only when the event is triggered
 * by a zoom or pan action, only for the Y Axes.

 * @example
 * import { useAfterSetExtremesY } from "./useAfterSetExtremes";
 *
 * const onAfterSetExtremes = useCallback((e: Highcharts.AxisSetExtremesEventObject) => {
 *  // ...
 * }, []);
 *
 * const onLayoutEffect = useCallback((yAxes: Highcharts.Axis[]) => {
 *  // ...
 * }, []);
 *
 * useAfterSetExtremesY(chart, onAfterSetExtremes, {
 *  onLayoutEffect,
 *  yAxesFilter: (axis: Highcharts.Axis) => axis.options.id !== "custom-id-you-dont-want",
 * });
 *
 * @param chart Current chart to bind the event to
 * @param onAfterSetExtremes event handler
 * @param options
 * @param options.onLayoutEffect callback called after the event handler is bound to the chart, on React.useLayoutEffect
 * @param options.yAxesFilter filter to apply on the yAxes to bind the event handler to.
 */
export const useAfterSetExtremesY = (
  chart: React.RefObject<Highcharts.Chart>,
  onAfterSetExtremes: (e: Highcharts.AxisSetExtremesEventObject) => void,
  {
    onLayoutEffect,
    yAxesFilter = () => true,
  }: {
    onLayoutEffect?: (e: Highcharts.Axis[]) => void;
    yAxesFilter: (axis: Highcharts.Axis) => boolean;
  },
) => {
  const afterSetExtremesHandler = useDebouncedCallback(
    (e: Highcharts.AxisSetExtremesEventObject) =>
      ["zoom", "pan"].includes(e.trigger) && onAfterSetExtremes(e),
    [onAfterSetExtremes],
    150,
  );

  useLayoutEffect(() => {
    const yAxes = chart.current?.yAxis.filter(yAxesFilter) ?? [];
    const removeHandlers = yAxes.map(axis =>
      Highcharts.addEvent(axis, "afterSetExtremes", afterSetExtremesHandler),
    );

    onLayoutEffect?.(yAxes);

    return () => {
      void removeHandlers.map(removeHandler => removeHandler());
    };
  }, [afterSetExtremesHandler, chart, onLayoutEffect, yAxesFilter]);
};
